import { DEFAULT_PRICES } from './pricing';

export const EUROZONE_COUNTRIES = [
  'AT',
  'BE',
  'HR',
  'CY',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PT',
  'SK',
  'SI',
  'ES',
];

export const PRICING_REGION_ORDER = [
  'US_GLOBAL',
  'UK',
  'EUROZONE',
  'KUWAIT',
  'UAE',
  'TURKEY',
];

export const PRICING_REGIONS = {
  US_GLOBAL: {
    id: 'US_GLOBAL',
    label: 'US / Global',
    currencyCode: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    countries: ['US'],
    isDefault: true,
    isActive: true,
  },
  UK: {
    id: 'UK',
    label: 'United Kingdom',
    currencyCode: 'GBP',
    currencySymbol: '£',
    locale: 'en-GB',
    countries: ['GB'],
    isDefault: false,
    isActive: true,
  },
  EUROZONE: {
    id: 'EUROZONE',
    label: 'Eurozone',
    currencyCode: 'EUR',
    currencySymbol: '€',
    locale: 'en-IE',
    countries: EUROZONE_COUNTRIES,
    isDefault: false,
    isActive: true,
  },
  KUWAIT: {
    id: 'KUWAIT',
    label: 'Kuwait',
    currencyCode: 'KWD',
    currencySymbol: 'KD',
    locale: 'en-KW',
    countries: ['KW'],
    isDefault: false,
    isActive: true,
  },
  UAE: {
    id: 'UAE',
    label: 'United Arab Emirates',
    currencyCode: 'AED',
    currencySymbol: 'AED',
    locale: 'en-AE',
    countries: ['AE'],
    isDefault: false,
    isActive: true,
  },
  TURKEY: {
    id: 'TURKEY',
    label: 'Turkey',
    currencyCode: 'TRY',
    currencySymbol: '₺',
    locale: 'tr-TR',
    countries: ['TR'],
    isDefault: false,
    isActive: true,
  },
};

export const CURRENCY_TO_REGION = {
  USD: 'US_GLOBAL',
  GBP: 'UK',
  EUR: 'EUROZONE',
  KWD: 'KUWAIT',
  AED: 'UAE',
  TRY: 'TURKEY',
};

export const PACKAGE_REGION_PRICES = {
  US_GLOBAL: { LAUNCH: 1490, BUILD: 2990, SCALE: 5990, CUSTOM: 890 },
  UK: { LAUNCH: 1190, BUILD: 2490, SCALE: 4990, CUSTOM: 710 },
  EUROZONE: { LAUNCH: 1290, BUILD: 2690, SCALE: 5490, CUSTOM: 770 },
  KUWAIT: { LAUNCH: 450, BUILD: 850, SCALE: 1500, CUSTOM: 270 },
  UAE: { LAUNCH: 4900, BUILD: 9900, SCALE: 18900, CUSTOM: 2900 },
  TURKEY: { LAUNCH: 49900, BUILD: 99900, SCALE: 199900, CUSTOM: 29900 },
};

export const PLAN_REGION_PRICES = {
  US_GLOBAL: { CARE: 79, GROWTH: 149, 'BUSINESS PRO': 299 },
  UK: { CARE: 69, GROWTH: 129, 'BUSINESS PRO': 249 },
  EUROZONE: { CARE: 79, GROWTH: 149, 'BUSINESS PRO': 299 },
  KUWAIT: { CARE: 25, GROWTH: 45, 'BUSINESS PRO': 90 },
  UAE: { CARE: 290, GROWTH: 550, 'BUSINESS PRO': 1100 },
  TURKEY: { CARE: 2990, GROWTH: 5490, 'BUSINESS PRO': 10990 },
};

const MODULE_PRICE_RULES = {
  US_GLOBAL: { multiplier: 1, step: 10 },
  UK: { multiplier: 0.8, step: 10 },
  EUROZONE: { multiplier: 0.87, step: 10 },
  KUWAIT: { multiplier: 0.3, step: 5 },
  UAE: { multiplier: 3.3, step: 100 },
  TURKEY: { multiplier: 28, step: 500 },
};

export const MODULE_PRICE_CAPS = {
  US_GLOBAL: 370,
  UK: 300,
  EUROZONE: 320,
  KUWAIT: 110,
  UAE: 1200,
  TURKEY: 12000,
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  KWD: 'KD',
  TRY: '₺',
};

const REGION_STORAGE_KEY = 'webouse-pricing-region';

const roundToStep = (value, step) =>
  Math.max(step, Math.round(Number(value || 0) / step) * step);

const normalizeKey = (value) => String(value || '').trim().toUpperCase();

export const getStoredPricingRegion = () => {
  if (typeof window === 'undefined') return null;
  const saved = window.localStorage.getItem(REGION_STORAGE_KEY);
  return PRICING_REGIONS[saved] ? saved : null;
};

export const storePricingRegion = (regionId) => {
  if (typeof window === 'undefined') return;
  if (PRICING_REGIONS[regionId]) {
    window.localStorage.setItem(REGION_STORAGE_KEY, regionId);
  }
};

export const normalizeRegionId = (regionId) =>
  PRICING_REGIONS[regionId] ? regionId : 'US_GLOBAL';

export const getRegionByCurrency = (currencyCode) =>
  CURRENCY_TO_REGION[String(currencyCode || '').toUpperCase()] || 'US_GLOBAL';

export const countryToRegionId = (countryCode) => {
  const code = String(countryCode || '').toUpperCase();
  if (code === 'GB') return 'UK';
  if (code === 'KW') return 'KUWAIT';
  if (code === 'AE') return 'UAE';
  if (code === 'TR') return 'TURKEY';
  if (EUROZONE_COUNTRIES.includes(code)) return 'EUROZONE';
  return 'US_GLOBAL';
};

export const normalizeApiRegion = (region) => {
  if (!region) return null;
  const currencyCode = region.currencyCode || region.currency_code;
  return {
    id: region.id,
    label: region.label,
    currencyCode,
    currencySymbol:
      CURRENCY_SYMBOLS[currencyCode] ||
      region.currencySymbol ||
      region.currency_symbol ||
      currencyCode,
    locale: region.locale,
    countries: region.countries || [],
    isDefault: region.isDefault ?? region.is_default ?? false,
    isActive: region.isActive ?? region.is_active ?? true,
    updatedAt: region.updatedAt || region.updated_at,
  };
};

export const mergeApiRegions = (regions = []) => {
  const merged = { ...PRICING_REGIONS };
  regions.map(normalizeApiRegion).filter(Boolean).forEach((region) => {
    merged[region.id] = { ...merged[region.id], ...region };
  });
  return PRICING_REGION_ORDER.map((id) => merged[id]).filter(Boolean);
};

export const getRegionConfig = (regionId, regions = null) => {
  const normalized = normalizeRegionId(regionId);
  if (Array.isArray(regions)) {
    return regions.find((region) => region.id === normalized) || PRICING_REGIONS[normalized];
  }
  return PRICING_REGIONS[normalized];
};

export const formatRegionalPrice = (value, regionId, regions = null) => {
  const region = getRegionConfig(regionId, regions);
  const amount = Number(value || 0);
  const currencyDisplay = ['KWD', 'AED'].includes(region.currencyCode)
    ? 'code'
    : 'narrowSymbol';

  return new Intl.NumberFormat(region.locale || 'en-US', {
    style: 'currency',
    currency: region.currencyCode,
    currencyDisplay,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);
};

export const getCurrencySelectorLabel = (region) => {
  const currencyCode = region?.currencyCode || region?.currency_code || '';
  const currencySymbol =
    region?.currencySymbol || region?.currency_symbol || currencyCode;
  return currencySymbol && currencySymbol !== currencyCode
    ? `${currencySymbol} ${currencyCode}`
    : currencyCode;
};

export const seedModuleRegionPrice = (usdPrice, regionId) => {
  const normalized = normalizeRegionId(regionId);
  const rule = MODULE_PRICE_RULES[normalized] || MODULE_PRICE_RULES.US_GLOBAL;
  return Math.min(
    roundToStep(Number(usdPrice || 0) * rule.multiplier, rule.step),
    MODULE_PRICE_CAPS[normalized] || MODULE_PRICE_CAPS.US_GLOBAL
  );
};

export const getFallbackRegionalPrice = (item, itemType, regionId) => {
  const normalized = normalizeRegionId(regionId);
  const canonical = normalizeKey(item?.canonicalName || item?.name || item?.item_key);
  const usdPrice = Number(item?.basePriceUsd ?? item?.price ?? 0);

  if (itemType === 'PACKAGE') {
    return PACKAGE_REGION_PRICES[normalized]?.[canonical] ?? usdPrice;
  }
  if (itemType === 'MONTHLY_PLAN') {
    return PLAN_REGION_PRICES[normalized]?.[canonical] ?? usdPrice;
  }
  return seedModuleRegionPrice(usdPrice, normalized);
};

export const getPriceKeys = (item) =>
  [
    item?.id,
    item?.canonicalName,
    item?.name,
    item?.item_key,
    item?.itemName,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toUpperCase());

export const findRegionalPrice = (prices = [], item, itemType, regionId) => {
  const keys = getPriceKeys(item);
  const normalized = normalizeRegionId(regionId);
  return prices.find((price) => {
    if (price.region_id !== normalized || price.item_type !== itemType) return false;
    const rowKeys = getPriceKeys({
      id: price.item_id,
      canonicalName: price.item_key,
      name: price.item_name,
    });
    return rowKeys.some((key) => keys.includes(key));
  });
};

export const getRegionalAmount = (item, itemType, regionId, prices = []) => {
  const normalized = normalizeRegionId(regionId);
  const canonical = normalizeKey(item?.canonicalName || item?.name || item?.item_key);
  const price = findRegionalPrice(prices, item, itemType, regionId);
  let amount;
  if (price?.amount !== undefined && price?.amount !== null) {
    amount = Number(price.amount);
  } else if (price?.amount_minor !== undefined && price?.minor_unit !== undefined) {
    amount = Number(price.amount_minor) / 10 ** Number(price.minor_unit || 0);
  } else {
    amount = getFallbackRegionalPrice(item, itemType, normalized);
  }

  if (itemType === 'PACKAGE' && canonical === 'CUSTOM') {
    return PACKAGE_REGION_PRICES[normalized]?.CUSTOM ?? amount;
  }
  if (itemType === 'MODULE') {
    const publicModulePrice = seedModuleRegionPrice(
      Number(item?.basePriceUsd ?? item?.price ?? amount),
      normalized
    );
    return Math.min(
      amount,
      publicModulePrice,
      MODULE_PRICE_CAPS[normalized] || MODULE_PRICE_CAPS.US_GLOBAL
    );
  }
  return amount;
};

export const applyRegionalPrice = (item, itemType, regionId, prices = [], regions = null) => {
  if (!item) return item;
  const normalized = normalizeRegionId(regionId);
  const region = getRegionConfig(normalized, regions);
  const basePriceUsd = Number(item.basePriceUsd ?? item.price ?? 0);
  const price = getRegionalAmount(
    { ...item, price: basePriceUsd },
    itemType,
    normalized,
    prices
  );

  return {
    ...item,
    price,
    basePriceUsd,
    pricingRegion: normalized,
    currencyCode: region.currencyCode,
  };
};

export const regionalizeModuleGroups = (groups = [], regionId, prices = [], regions = null) =>
  groups.map((group) => ({
    ...group,
    modules: (group.modules || []).map((module) =>
      applyRegionalPrice(module, 'MODULE', regionId, prices, regions)
    ),
  }));

export const getDefaultRegionOptions = () =>
  PRICING_REGION_ORDER.map((id) => PRICING_REGIONS[id]);

export const DEFAULT_REGIONAL_USD_TOTALS = {
  packages: DEFAULT_PRICES.packages,
  plans: DEFAULT_PRICES.plans,
  modules: DEFAULT_PRICES.modules,
};
